import { PassingTime } from '@/features/trips/PassingTime';
import { Trip } from '@/features/trips/Trip';

export interface PendingPassingTime {
    trip: Trip;
    passingTime: PassingTime;
    timeStamp: number;
}

export type PassingTimeSender = (pending: PendingPassingTime) => Promise<unknown>;

// Long enough to merge a ghost-click/double-tap, short enough to feel instant.
export const PASSING_TIME_DEBOUNCE_MS = 300;

export function createPassingTimeCoalescer(send: PassingTimeSender, debounceMs: number = PASSING_TIME_DEBOUNCE_MS) {
    const pending = new Map<string, PendingPassingTime>();
    const timers = new Map<string, ReturnType<typeof setTimeout>>();

    function keyFor(item: PendingPassingTime): string {
        return `${item.trip.tripId}|${item.passingTime.stop.quayRef}|${item.passingTime.passingTime}`;
    }

    function flushKey(key: string): void {
        const timer = timers.get(key);
        if (timer !== undefined) {
            clearTimeout(timer);
            timers.delete(key);
        }

        const item = pending.get(key);
        if (item !== undefined) {
            pending.delete(key);
            void send(item).catch(() => {
                // Fire-and-forget: the Workbox offline queue handles retries.
            });
        }
    }

    return {
        schedule(item: PendingPassingTime): void {
            const key = keyFor(item);
            pending.set(key, item);

            const existing = timers.get(key);
            if (existing !== undefined) {
                clearTimeout(existing);
            }
            timers.set(
                key,
                setTimeout(() => flushKey(key), debounceMs)
            );
        },
        flush(): void {
            for (const key of Array.from(timers.keys())) {
                flushKey(key);
            }
        }
    };
}
